import createSmimeDefaultViewState, { generateSmimeViewState } from './createSmimeDefaultViewState';
import getSmimeType from './getSmimeType';
import isClearSignedItem from './isClearSignedItem';
import isSmimeDecoded from './isSmimeDecoded';
import isSMIMEItem from './isSMIMEItem';
import { isSmimeAdminSettingEnabled, isSmimeUserSettingEnabled } from './smimeSettingsUtil';
import SmimeViewState from '../store/schema/SmimeViewState';
import { ClientItem } from 'owa-mail-store';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import isSmimeAdapterUsable from 'owa-smime-adapter/lib/utils/isSmimeAdapterUsable';
import SmimeErrorStateEnum from 'owa-smime-adapter/lib/store/schema/SmimeErrorStateEnum';

export const mergeSmimeViewStates = (smimeViewStates: SmimeViewState[]): SmimeViewState => {
    if (!smimeViewStates || !smimeViewStates.length) {
        return null;
    }

    const shouldEncryptMessageAsSmime = smimeViewStates.some(
        state => state?.shouldEncryptMessageAsSmime
    );
    const shouldSignMessageAsSmime = smimeViewStates.some(state => state?.shouldSignMessageAsSmime);

    /**
     * smimeViewStates receive the viewStates in the priority order
     * to be merged. We select the first error code received in the
     * smimeViewStates to display the info bar.
     */
    const errorCode = smimeViewStates.map(state => state?.errorCode).filter(code => !!code)[0];

    return generateSmimeViewState(shouldEncryptMessageAsSmime, shouldSignMessageAsSmime, errorCode);
};

export const getSmimeViewStateWithAdminSettings = (): SmimeViewState => {
    if (isSmimeAdminSettingEnabled()) {
        /**
         * When S/MIME admin settings are enforced we will
         * always respond with an S/MIME mail even when adapter is not usable.
         */
        const userConfig = getUserConfiguration();
        return generateSmimeViewState(
            userConfig.SmimeAdminSettings.AlwaysEncrypt,
            userConfig.SmimeAdminSettings.AlwaysSign,
            isSmimeAdapterUsable() ? SmimeErrorStateEnum.NoError : SmimeErrorStateEnum.ControlError
        );
    }
    return null;
};

export const getSmimeViewStateWithUserSettings = (): SmimeViewState => {
    if (isSmimeUserSettingEnabled()) {
        if (isSmimeAdapterUsable()) {
            /**
             * When S/MIME user settings are enforced we will only respond
             * with an S/MIME mail when S/MIME adapter is usable.
             * TODO: Add appropriate infobar when S/MIME adapter is not usable #45703
             */
            const userConfig = getUserConfiguration();
            return generateSmimeViewState(
                userConfig.UserOptions.SmimeEncrypt,
                userConfig.UserOptions.SmimeSign
            );
        }
        return generateSmimeViewState(
            false /*shouldEncryptMessageAsSmime*/,
            false /*shouldSignMessageAsSmime*/,
            SmimeErrorStateEnum.warningUserSettingNotApplied
        );
    }
    return null;
};

/**
 * This util generates the SmimeViewState for the new response
 * if the parent item is of S/MIME type.
 *
 * @param parentItem Item for which we are responding currently.
 */
export const getSmimeViewStateFromParentItem = (parentItem?: ClientItem): SmimeViewState => {
    if (isSMIMEItem(parentItem)) {
        if (
            isSmimeAdapterUsable() &&
            (isSmimeDecoded(parentItem) || isClearSignedItem(parentItem))
        ) {
            /**
             * For cases where decoding fails other than clear sign
             * we don't know the smimeType since it is available once
             * message decoded successfully, therefore in that case we cannot
             * reply with parent SmimeType.
             */
            return createSmimeDefaultViewState(getSmimeType(parentItem));
        }
        return generateSmimeViewState(
            false /*shouldEncryptMessageAsSmime*/,
            false /*shouldSignMessageAsSmime*/,
            SmimeErrorStateEnum.warningReplyOrForwardNotSmime
        );
    }
    return null;
};
